<script setup lang="ts">
import { Co2Results, QuestionType } from '@/models/co2-quiz.model';
import { User, UserCo2QuizAnswer } from '@/models/user.model';
import { ShiftCMSState } from '@/store';
import { UsersActions, UsersState } from '@/stores/users.store';
import { computed, onMounted, PropType } from 'vue';
import { useStore } from 'vuex';

const props = defineProps({
  user: {
    type: Object as PropType<User>,
    required: true,
  },
});

const store = useStore<ShiftCMSState & { us: UsersState }>();

const questions = computed<UserCo2QuizAnswer[]>(
  () => store.state.us.userCo2QuizAnswers,
);
const results = computed<Co2Results[]>(() => store.state.us.userCo2Results);

const headers = [
  { title: 'Questions', key: 'question.title' },
  { title: 'Answer', key: 'answer' },
  { title: 'Options', key: 'options' },
];

const resultHeaders = [
  { title: 'Date', key: 'resultsDate' },
  { title: 'Complete', key: 'isComplete' },
  { title: 'Valid', key: 'isValid' },
  { title: 'Options', key: 'options' },
];

const emit = defineEmits(['reset']);

function resetCo2Quiz() {
  if (
    confirm(`Are you sure you want to reset this user's Co2 Quiz information`)
  ) {
    store.dispatch(UsersActions.ResetCo2Quiz, props.user);

    emit('reset');
  }
}

function deleteUserCo2Question(id: number) {
  store.dispatch(UsersActions.DeleteUserCo2Answer, {
    id,
    userId: props.user.id,
  });
}

function deleteUserCo2Results(id: number) {
  store.dispatch(UsersActions.DeleteUserCo2Results, {
    id,
    userId: props.user.id,
  });
}

onMounted(() => {
  store.dispatch(UsersActions.GetUserCo2Results, props.user.id);
});
</script>
<template>
  <v-card class="mt-4">
    <v-card-title
      >Co2 Quiz
      <v-btn color="warning" @click="resetCo2Quiz">Reset</v-btn></v-card-title
    >

    <v-data-table
      density="compact"
      :headers="headers"
      :items="questions"
      itemsPerPage="10"
      hover
    >
      <template v-slot:item.answer="{ item }">
        {{
          item.question.questionType === QuestionType.UserInput ||
          item.answer?.isOther
            ? item.inputValue
            : item.answer?.text
        }}
      </template>
      <template v-slot:item.options="{ item }">
        <v-btn
          density="compact"
          icon="mdi-delete-outline"
          @click="deleteUserCo2Question(item.id)"
        />
      </template>
    </v-data-table>
    <v-card-title>Co2 Results</v-card-title>
    <v-data-table
      density="compact"
      :headers="resultHeaders"
      :items="results"
      itemsPerPage="10"
      hover
    >
      <template v-slot:item.isComplete="{ item }">
        <v-icon icon="mdi-check-bold" v-if="item.isComplete" color="success" />
        <v-icon
          icon="mdi-close-thick"
          v-if="!item.isComplete"
          color="warning"
        />
      </template>
      <template v-slot:item.isValid="{ item }">
        <v-icon icon="mdi-check-bold" v-if="item.isValid" color="success" />
        <v-icon icon="mdi-close-thick" v-if="!item.isValid" color="warning" />
      </template>
      <template v-slot:item.options="{ item }">
        <v-btn
          density="compact"
          icon="mdi-delete-outline"
          @click="deleteUserCo2Results(item.id)"
        />
      </template>
    </v-data-table>
  </v-card>
</template>
